import React, { useRef, useEffect } from 'react';
import { ArrowUp, Users } from 'lucide-react';
import type { BrandTeamChatMessage } from '../../../../services/brandTeamService';

interface ChatPanelProps {
  messages: BrandTeamChatMessage[];
  chatInput: string;
  onInputChange: (value: string) => void;
  onSend: () => void;
  sending: boolean;
  disabled: boolean;
}

const ChatPanel: React.FC<ChatPanelProps> = ({ messages, chatInput, onInputChange, onSend, sending, disabled }) => {
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (chatInput.trim() && !sending && !disabled) onSend();
    }
  };

  return (
    <div className="flex h-full w-full lg:w-[300px] flex-shrink-0 flex-col lg:border-l border-primary-100 bg-white">
      {/* Header */}
      <div className="flex items-center gap-3 border-b border-primary-100 px-4 py-3.5">
        <div className="flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-br from-brand-purple to-brand-blue">
          <Users className="h-4 w-4 text-white" />
        </div>
        <div className="flex-1">
          <div className="text-sm font-bold text-text-dark">Lena</div>
          <div className="text-[10px] font-medium text-brand-purple">Brand Manager</div>
        </div>
        <span className="h-2.5 w-2.5 rounded-full bg-green-500 shadow-[0_0_0_2px_white,0_0_0_3px_#22c55e]" />
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
        {messages.map((msg) => (
          <div key={msg.id} className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-xs leading-relaxed ${
              msg.sender === 'user'
                ? 'bg-brand-purple text-white rounded-br-sm'
                : 'bg-primary-50 text-text-dark rounded-bl-sm'
            }`}>
              {msg.sender !== 'user' && (
                <div className="text-[10px] font-semibold text-brand-purple mb-1">Lena</div>
              )}
              {msg.message}
            </div>
          </div>
        ))}
        {messages.length === 0 && (
          <div className="flex h-full items-center justify-center">
            <p className="text-xs text-gray-400 text-center">Chat with your Brand Manager<br />about this session</p>
          </div>
        )}
        <div ref={endRef} />
      </div>

      {/* Input */}
      <div className="border-t border-primary-100 px-3 py-3">
        <div className="flex gap-2">
          <input
            type="text"
            value={chatInput}
            onChange={(e) => onInputChange(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ask Lena anything..."
            disabled={disabled}
            className="flex-1 rounded-xl border border-primary-100 bg-gray-50 px-3.5 py-2.5 text-xs outline-none transition-colors focus:border-brand-purple focus:bg-white focus:ring-2 focus:ring-brand-purple/20 disabled:opacity-50"
          />
          <button
            type="button"
            onClick={onSend}
            disabled={!chatInput.trim() || sending || disabled}
            className="flex h-[38px] w-[38px] flex-shrink-0 items-center justify-center rounded-xl bg-brand-purple text-white transition-opacity disabled:opacity-40 hover:opacity-90"
          >
            <ArrowUp className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChatPanel;
